import React, { useMemo } from 'react';

import { getLeadSourceLabel } from '@troia-v3/types';

import { useFunnelsDropdown, useStepsByFunnelId } from '@/modules/funnels/hooks';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { Icon } from '@/shared/icons';

import { formatCurrencyBRL, formatShortDate, temperatureLabel } from '../detailsFormat';
import type { DetailsData } from '../useDetailsData';

interface DealLayerProps {
  data: DetailsData;
  onOpenCrm: (leadId: string) => void;
}

const QUALIFICACOES: Record<string, string> = {
  pending: 'Pendente',
  qualified: 'Qualificado',
  disqualified: 'Desqualificado',
};

/** Acima disto o número deixa de ser informação e passa a ser aviso. */
const DIAS_PARADO_AVISO = 7;

/** Aviso legível nos dois temas: puxa o token de aviso na direção do texto. */
const COR_AVISO = 'color-mix(in srgb, var(--theme-warning-color) 60%, var(--theme-card-text))';

interface LinhaProps {
  rotulo: string;
  testId: string;
  children: React.ReactNode;
}

const Linha: React.FC<LinhaProps> = ({ rotulo, testId, children }) => (
  <div className="cdp-kv">
    <span className="cdp-k">{rotulo}</span>
    <span className="cdp-v" data-testid={testId}>
      {children}
    </span>
  </div>
);

/**
 * Camada Negócio — o que o painel de hoje não mostra: o lead.
 *
 * Duas escolhas de dado que valem comentário:
 *
 * 1. O valor é `wonValue ?? budget`, nessa ordem. `wonValue` só existe em lead
 *    ganho; enquanto o negócio corre, o número que existe é o orçamento. Ler só
 *    um dos dois deixaria metade dos leads com o valor em branco.
 *
 * 2. Linha sem dado **não é renderizada**. Uma coluna de travessões ocupa a
 *    mesma altura de uma coluna de fatos e não diz nada — quem abre esta camada
 *    quer achar o que existe, não conferir o que falta.
 *
 * As três buscas (etapas, funis e usuários) ficam presas ao que o lead tem:
 * sem funil não se pede etapa, sem responsável não se pede a lista de gente.
 * Elas ainda compartilham a chave de cache com o cabeçalho do chat, que já
 * carrega funis e etapas — na prática vêm da memória, sem ir à rede.
 */
const DealLayer: React.FC<DealLayerProps> = ({ data, onOpenCrm }) => {
  const { lead } = data;
  const funnelId = lead?.funnelId;
  const assigneeId = lead?.assigneeId;

  const { data: steps = [] } = useStepsByFunnelId(funnelId);
  const { data: funnels = [] } = useFunnelsDropdown({ enabled: !!funnelId });
  const { data: users = [] } = useUsersDropdown({ status: 'active', enabled: !!assigneeId });

  // A régua só faz sentido na ordem do funil; a rota não promete ordenação.
  // `order` é obrigatório no tipo, mas etapa gravada sem ele daria `NaN` no
  // comparador — e `NaN` desordena a lista inteira, não só a etapa faltante.
  // Mesma guarda do `ConversationStageSelector`, que lê a mesma rota.
  const etapas = useMemo(
    () => [...steps].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [steps],
  );

  const indiceAtual = etapas.findIndex((etapa) => etapa.id === lead?.stepId);
  const nomeEtapa = etapas.find((etapa) => etapa.id === lead?.stepId)?.name ?? lead?.step?.name;
  const nomeFunil = funnels.find((funil) => funil.id === funnelId)?.name ?? lead?.funnel?.name;
  // O lead já vem com o responsável resolvido em muitos caminhos da API; o
  // dropdown é a fonte principal e o campo do próprio lead é a rede de baixo.
  const responsavel = users.find((user) => user.id === assigneeId)?.name ?? lead?.assignee?.name;

  if (!lead) {
    return (
      <p className="cdp-empty" data-testid="details-deal-empty">
        Este contato não está em nenhum funil.
      </p>
    );
  }

  const temperatura = lead.temperature;
  const diasNaEtapa = lead.daysInStep;
  const qualificacao = lead.qualifyStatus ? (QUALIFICACOES[lead.qualifyStatus] ?? lead.qualifyStatus) : undefined;
  const totalInteresses = lead.interests?.length ?? 0;

  return (
    <div data-testid="details-deal">
      {nomeFunil && (
        <div className="text-xs" style={{ color: 'var(--theme-muted-text)' }} data-testid="details-deal-funnel">
          {nomeFunil}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap mt-1">
        <span
          style={{ fontSize: 20, fontWeight: 700, letterSpacing: '-.02em', color: 'var(--theme-card-text)' }}
          data-testid="details-deal-value"
        >
          {formatCurrencyBRL(lead.wonValue ?? lead.budget)}
        </span>
        {temperatura && (
          <span className="cdp-tag" data-testid="details-deal-temperature">
            {temperatura === 'hot' && <Icon name="flame" className="w-3 h-3" />}
            {temperatureLabel(temperatura)}
          </span>
        )}
      </div>

      {etapas.length > 0 ? (
        <>
          {/* A régua é decorativa: o mesmo fato está escrito logo abaixo, em
              "<etapa> · <n> de <total>", que é o que o leitor de tela anuncia. */}
          <div className="flex items-center gap-1 mt-3" data-testid="details-deal-steps" aria-hidden="true">
            {etapas.map((etapa, indice) => (
              <i
                key={etapa.id}
                className="flex-1 rounded-full"
                style={{
                  height: 4,
                  background:
                    indice <= indiceAtual ? 'var(--theme-primary)' : 'var(--theme-surface-sunken)',
                }}
              />
            ))}
          </div>
          <div className="flex items-baseline justify-between gap-2 mt-1.5">
            <span
              className="text-[13px] font-semibold truncate"
              style={{ color: 'var(--theme-card-text)' }}
              data-testid="details-deal-step"
            >
              {nomeEtapa ?? 'Sem etapa'}
            </span>
            {indiceAtual >= 0 && (
              <span
                className="text-[11px] flex-none"
                style={{ color: 'var(--theme-muted-text)' }}
                data-testid="details-deal-step-count"
              >
                {indiceAtual + 1} de {etapas.length}
              </span>
            )}
          </div>
        </>
      ) : (
        nomeEtapa && (
          <div
            className="text-[13px] font-semibold mt-3"
            style={{ color: 'var(--theme-card-text)' }}
            data-testid="details-deal-step"
          >
            {nomeEtapa}
          </div>
        )
      )}

      <div className="mt-3">
        {typeof diasNaEtapa === 'number' && (
          <Linha rotulo="Dias na etapa" testId="details-deal-days">
            <span style={diasNaEtapa > DIAS_PARADO_AVISO ? { color: COR_AVISO } : undefined}>
              {diasNaEtapa === 1 ? '1 dia' : `${diasNaEtapa} dias`}
            </span>
          </Linha>
        )}

        {responsavel && (
          <Linha rotulo="Responsável" testId="details-deal-assignee">
            {responsavel}
          </Linha>
        )}

        {lead.source && (
          <Linha rotulo="Origem" testId="details-deal-source">
            {getLeadSourceLabel(lead.source)}
          </Linha>
        )}

        {lead.campaignName && (
          <Linha rotulo="Campanha" testId="details-deal-campaign">
            {lead.campaignName}
          </Linha>
        )}

        {qualificacao && (
          <Linha rotulo="Qualificação" testId="details-deal-qualify">
            {qualificacao}
          </Linha>
        )}

        {typeof lead.score === 'number' && Number.isFinite(lead.score) && (
          <Linha rotulo="Score" testId="details-deal-score">
            {lead.score}
          </Linha>
        )}

        {lead.lastActivityAt && (
          <Linha rotulo="Última atividade" testId="details-deal-last-activity">
            {formatShortDate(lead.lastActivityAt)}
          </Linha>
        )}

        {/* `earliestPendingActivityAt` é a pendência MAIS ANTIGA e pode estar
            vencida. Chamar de "próxima" mostraria data no passado sob um rótulo
            de futuro — o card do CRM trata esse mesmo caso como atrasado. */}
        {lead.earliestPendingActivityAt && (
          <Linha rotulo="Atividade pendente" testId="details-deal-next-activity">
            {formatShortDate(lead.earliestPendingActivityAt)}
          </Linha>
        )}
      </div>

      {totalInteresses > 0 && (
        <div className="cdp-line" data-testid="details-deal-interests">
          <span className="cdp-lineic">
            <Icon name="target" className="w-4 h-4" />
          </span>
          <span className="text-[13px]" style={{ color: 'var(--theme-card-text)' }}>
            {totalInteresses === 1 ? '1 interesse registrado' : `${totalInteresses} interesses registrados`}
          </span>
        </div>
      )}

      <button
        type="button"
        className="cdp-btn mt-4"
        onClick={() => onOpenCrm(lead.id)}
        data-testid="details-open-crm"
      >
        <Icon name="external-link" className="w-4 h-4" />
        Abrir negócio no CRM
      </button>
    </div>
  );
};

export default DealLayer;
